import polyline from '@mapbox/polyline';

const isValidCoordinate = value => typeof value === 'number' && !Number.isNaN(value);

const isViaPoint = point => point?.type?.id === 'via' || point?.action?.id === 'via';

const buildViaIdentifier = (waypoint, index) => {
  const lat = Number(waypoint?.lat);
  const lng = Number(waypoint?.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return `via-${index}`;
  }

  return `via-${index}-${lat.toFixed(6)}-${lng.toFixed(6)}`;
};

const getDistanceScore = (point, waypoint) => {
  if (!point || !waypoint) return Number.POSITIVE_INFINITY;

  const latDelta = Number(point.lat) - Number(waypoint.lat);
  const lngDelta = Number(point.lng) - Number(waypoint.lng);

  return (latDelta * latDelta) + (lngDelta * lngDelta);
};

const COORDINATE_MATCH_TOLERANCE = 0.00001;

const hasMatchingPointCoordinate = (points = [], waypoint) =>
  (points || []).some(point =>
    Math.abs(Number(point?.lat) - Number(waypoint?.lat)) <= COORDINATE_MATCH_TOLERANCE &&
    Math.abs(Number(point?.lng) - Number(waypoint?.lng)) <= COORDINATE_MATCH_TOLERANCE,
  );

const getPointSegmentDistanceScore = (point, start, end) => {
  if (!point || !start || !end) return Number.POSITIVE_INFINITY;

  const segmentLat = Number(end.lat) - Number(start.lat);
  const segmentLng = Number(end.lng) - Number(start.lng);
  const segmentLength = (segmentLat * segmentLat) + (segmentLng * segmentLng);

  if (segmentLength === 0) {
    return getDistanceScore(point, start);
  }

  const rawProjection =
    ((Number(point.lat) - Number(start.lat)) * segmentLat +
      (Number(point.lng) - Number(start.lng)) * segmentLng) /
    segmentLength;
  const projection = Math.max(0, Math.min(1, rawProjection));
  const projectedLat = Number(start.lat) + projection * segmentLat;
  const projectedLng = Number(start.lng) + projection * segmentLng;
  const latDelta = Number(point.lat) - projectedLat;
  const lngDelta = Number(point.lng) - projectedLng;

  return (latDelta * latDelta) + (lngDelta * lngDelta);
};

const getClosestSegmentInsertIndex = (points = [], waypoint) => {
  if (!Array.isArray(points) || points.length < 2) {
    return points.length;
  }

  let closestInsertIndex = points.length - 1;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (let index = 0; index < points.length - 1; index += 1) {
    const start = points[index];
    const end = points[index + 1];
    if (!isValidCoordinate(start?.lat) || !isValidCoordinate(start?.lng)) continue;
    if (!isValidCoordinate(end?.lat) || !isValidCoordinate(end?.lng)) continue;

    const distance = getPointSegmentDistanceScore(waypoint, start, end);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestInsertIndex = index + 1;
    }
  }

  return closestInsertIndex;
};

export const resolveInitialPoints = (points = []) =>
  Array.isArray(points) && points.length > 0 ? points : [];

const parseWaypointString = (waypoints) => {
  if (typeof waypoints !== 'string') return waypoints;

  const trimmedWaypoints = waypoints.trim();
  if (!trimmedWaypoints) return [];

  if (trimmedWaypoints.startsWith('[') || trimmedWaypoints.startsWith('{')) {
    try {
      return JSON.parse(trimmedWaypoints);
    } catch {
      return [];
    }
  }

  try {
    return polyline.decode(trimmedWaypoints).map(([lat, lng]) => ({ lat, lng }));
  } catch {
    return [];
  }
};

export const normalizeWaypoints = (waypoints = []) =>
  (() => {
    const parsedWaypoints = parseWaypointString(waypoints);
    return Array.isArray(parsedWaypoints) ? parsedWaypoints : [];
  })()
    .map((waypoint, index) => {
      if (!waypoint) return null;

      if (Array.isArray(waypoint) && waypoint.length >= 2) {
        const [lat, lng] = waypoint;
        const hasExplicitIndex = Number.isFinite(waypoint[2]);
        if (isValidCoordinate(lat) && isValidCoordinate(lng)) {
          return {
            lat,
            lng,
            index: hasExplicitIndex ? Number(waypoint[2]) : null,
            sourceOrder: index,
            hasExplicitIndex,
            identifier: waypoint[3] || null,
          };
        }
      }

      if (isValidCoordinate(waypoint.lat) && isValidCoordinate(waypoint.lng)) {
        const hasExplicitIndex =
          Number.isFinite(waypoint.index) || Number.isFinite(waypoint.order);

        return {
          lat: waypoint.lat,
          lng: waypoint.lng,
          index: hasExplicitIndex && Number.isFinite(waypoint.index)
            ? Number(waypoint.index)
            : hasExplicitIndex && Number.isFinite(waypoint.order)
              ? Number(waypoint.order)
              : null,
          sourceOrder: index,
          hasExplicitIndex,
          identifier: waypoint.identifier || waypoint.id || null,
        };
      }

      if (waypoint.latLng && isValidCoordinate(waypoint.latLng.lat) && isValidCoordinate(waypoint.latLng.lng)) {
        const hasExplicitIndex = Number.isFinite(waypoint.index);
        return {
          lat: waypoint.latLng.lat,
          lng: waypoint.latLng.lng,
          index: hasExplicitIndex ? Number(waypoint.index) : null,
          sourceOrder: index,
          hasExplicitIndex,
          identifier: waypoint.identifier || waypoint.id || null,
        };
      }

      return null;
    })
    .filter(Boolean);

const createViaPoint = (waypoint, index) => ({
  identifier: buildViaIdentifier(waypoint, index),
  type: { id: 'via', name: 'Intermediário' },
  action: { id: 'via', name: 'Intermediário' },
  lat: waypoint.lat,
  lng: waypoint.lng,
  formatted_address: '',
  address: '',
});

export const insertWaypointsIntoPoints = (
  basePoints = [],
  waypoints = [],
  { defaultCountry = null, viaName = 'Ponto intermediário' } = {},
) => {
  if (!Array.isArray(basePoints) || basePoints.length === 0) {
    return basePoints || [];
  }

  const normalizedWaypoints = normalizeWaypoints(waypoints);
  if (normalizedWaypoints.length === 0) {
    return basePoints;
  }

  const merged = [...basePoints];
  const sorted = [...normalizedWaypoints].sort((a, b) => {
    if (a.hasExplicitIndex && b.hasExplicitIndex) {
      return (a.index ?? 0) - (b.index ?? 0);
    }

    return (a.sourceOrder ?? 0) - (b.sourceOrder ?? 0);
  });
  const baseCountry = merged[0]?.country || defaultCountry;

  sorted.forEach((waypoint, index) => {
    if (!waypoint.hasExplicitIndex && hasMatchingPointCoordinate(merged, waypoint)) {
      return;
    }

    const insertIndexRaw = waypoint.hasExplicitIndex && Number.isFinite(waypoint.index)
      ? waypoint.index
      : getClosestSegmentInsertIndex(merged, waypoint);
    const insertIndex = Math.min(
      Math.max(1, insertIndexRaw),
      merged.length - 1,
    );

    merged.splice(insertIndex, 0, {
      identifier: waypoint.identifier || `via-${waypoint.index ?? index}-${waypoint.lat}-${waypoint.lng}`,
      type: { id: 'via', name: viaName },
      action: { id: 'via', name: viaName },
      country: baseCountry,
      address: '',
      number: '',
      complement: '',
      neighborhood: '',
      zip_code: '',
      city: null,
      cityId: null,
      province: '',
      lat: waypoint.lat,
      lng: waypoint.lng,
      formatted_address: '',
      start_schedule: '',
      scheduled_time: '',
    });
  });

  return merged;
};

const matchBusinessPointsToWaypointIndexes = (businessPoints = [], normalizedWaypoints = []) => {
  if (businessPoints.length === 0 || normalizedWaypoints.length === 0) {
    return [];
  }

  if (businessPoints.length === 1) {
    return [0];
  }

  const matchedIndexes = [0];
  let searchStartIndex = 1;

  for (let businessIndex = 1; businessIndex < businessPoints.length - 1; businessIndex += 1) {
    const point = businessPoints[businessIndex];
    const remainingBusinessPoints = businessPoints.length - businessIndex - 1;
    const lastCandidateIndex = normalizedWaypoints.length - remainingBusinessPoints - 1;

    let bestWaypointIndex = searchStartIndex;
    let bestDistanceScore = Number.POSITIVE_INFINITY;

    for (let waypointIndex = searchStartIndex; waypointIndex <= lastCandidateIndex; waypointIndex += 1) {
      const distanceScore = getDistanceScore(point, normalizedWaypoints[waypointIndex]);

      if (distanceScore < bestDistanceScore) {
        bestDistanceScore = distanceScore;
        bestWaypointIndex = waypointIndex;
      }
    }

    matchedIndexes.push(bestWaypointIndex);
    searchStartIndex = bestWaypointIndex + 1;
  }

  matchedIndexes.push(normalizedWaypoints.length - 1);

  return matchedIndexes;
};

export const reconcilePointsWithWaypoints = (points = [], waypoints = []) => {
  const normalizedWaypoints = normalizeWaypoints(waypoints);

  if (normalizedWaypoints.length === 0) {
    return points;
  }

  if (!Array.isArray(points) || points.length < 2) {
    return normalizedWaypoints.map((waypoint, index) => ({
      ...(points[index] || createViaPoint(waypoint, index)),
      lat: waypoint.lat,
      lng: waypoint.lng,
    }));
  }

  if (normalizedWaypoints.length === 1) {
    return [
      {
        ...points[0],
        lat: normalizedWaypoints[0].lat,
        lng: normalizedWaypoints[0].lng,
      },
    ];
  }

  const businessPoints = points.filter(point => !isViaPoint(point));

  if (businessPoints.length < 2 || normalizedWaypoints.length < businessPoints.length) {
    return normalizedWaypoints.map((waypoint, index) => ({
      ...(points[index] || createViaPoint(waypoint, index)),
      lat: waypoint.lat,
      lng: waypoint.lng,
    }));
  }

  const businessWaypointIndexes = matchBusinessPointsToWaypointIndexes(
    businessPoints,
    normalizedWaypoints,
  );

  let businessPointCursor = 0;
  let viaPointCursor = 0;
  const consumedViaIdentifiers = new Set();

  return normalizedWaypoints.map((waypoint, waypointIndex) => {
    const isBusinessWaypoint = businessWaypointIndexes[businessPointCursor] === waypointIndex;

    if (isBusinessWaypoint) {
      const businessPoint = businessPoints[businessPointCursor];
      businessPointCursor += 1;

      return {
        ...businessPoint,
        lat: waypoint.lat,
        lng: waypoint.lng,
      };
    }

    const viaPoint = points.find((point) => {
      if (!isViaPoint(point)) return false;
      if (point?.identifier && consumedViaIdentifiers.has(point.identifier)) return false;

      const matchesCoordinate =
        Number(point.lat) === Number(waypoint.lat) &&
        Number(point.lng) === Number(waypoint.lng);

      return matchesCoordinate;
    });

    const nextViaPoint = viaPoint || createViaPoint(waypoint, viaPointCursor);
    if (viaPoint?.identifier) {
      consumedViaIdentifiers.add(viaPoint.identifier);
    }
    viaPointCursor += 1;

    return {
      ...nextViaPoint,
      type: { id: 'via', name: 'Intermediário' },
      action: { id: 'via', name: 'Intermediário' },
      lat: waypoint.lat,
      lng: waypoint.lng,
    };
  });
};

export const hasCoordinateChanges = (previousPoints = [], nextPoints = []) => {
  if (previousPoints.length !== nextPoints.length) return true;

  for (let index = 0; index < previousPoints.length; index += 1) {
    const previous = previousPoints[index];
    const next = nextPoints[index];

    if (!previous || !next) return true;

    if (previous.lat !== next.lat || previous.lng !== next.lng) {
      return true;
    }
  }

  return false;
};
